// Frontend copy of the duplicate Control Strategy config selection rule.
// SYNC: must behave identically to functions/calculations/configSelection.js
// (used by the functions and, via the generated mirror, the Cloud Run daily job);
// src/util/__tests__/configSelection.test.js asserts the two agree. Duplicated
// because CRA cannot import from outside src/.
//
// Rule: the MOST RECENTLY UPDATED doc wins — the later of `updatedAt` (stamped on
// every create/update by src/util/db.js and by archiveData) and `createdAt`
// (legacy docs). Exact ties break on the larger doc id. An explicit
// `updatedAt: null` is a pending local serverTimestamp() (the save in flight) and
// counts as newest so the chart doesn't flip to another duplicate mid-save.

export function configTimestampMillis(value) {
  if (value == null) return -Infinity;
  if (typeof value === 'number') return Number.isFinite(value) ? value : -Infinity;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? -Infinity : t;
  }
  if (typeof value === 'object') {
    // eslint-disable-next-line no-underscore-dangle
    const secs = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    if (typeof secs === 'number') {
      const nanos =
        // eslint-disable-next-line no-underscore-dangle
        (typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds) || 0;
      return secs * 1000 + Math.floor(nanos / 1e6);
    }
    return -Infinity;
  }
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? -Infinity : t;
  }
  return -Infinity;
}

export function configRecencyMillis(data) {
  if (!data) return -Infinity;
  if (Object.prototype.hasOwnProperty.call(data, 'updatedAt') && data.updatedAt === null) {
    return Infinity;
  }
  return Math.max(configTimestampMillis(data.updatedAt), configTimestampMillis(data.createdAt));
}

// configs: formatted `{ id, ...data }` objects (src/util/db.js `format`).
// Returns the winning config (same object reference) or null. Never mutates input.
export function pickLatestControlStrategyConfig(configs) {
  if (!Array.isArray(configs)) return null;
  let best = null;
  let bestMillis = -Infinity;
  let bestId = '';
  for (const config of configs) {
    if (config != null) {
      const millis = configRecencyMillis(config);
      const id = String(config.id ?? '');
      if (best === null || millis > bestMillis || (millis === bestMillis && id > bestId)) {
        best = config;
        bestMillis = millis;
        bestId = id;
      }
    }
  }
  return best;
}

// { latest, duplicates } — duplicates are every other config, for clean-up.
export function splitLatestConfig(configs) {
  const latest = pickLatestControlStrategyConfig(configs);
  if (!latest) return { latest: null, duplicates: [] };
  return { latest, duplicates: configs.filter((c) => c != null && c !== latest) };
}

// Returns a copy of `data` with `updatedAt` set from `makeTimestamp()`
// (serverTimestamp in db.js). Does not mutate the caller's object, which is often
// live React state.
export function withUpdatedAt(data, makeTimestamp) {
  return { ...data, updatedAt: makeTimestamp() };
}
